// AppointmentDetail.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api.js";
import Error403Page from "./Error_backend";
import "../style/css/read_crud.css";
import "../style/css/base.css";
import PublicConfirmationModal from "./PublicConfirmationModal";
import AlertModal from "./AlertModal";

interface Service {
  id: string;
  name: string;
  description: string;
  price: string;
  duration_minutes: number;
  status: boolean;
  service_type: string | null;
  clinic: number;
}

interface Patient {
  id: string;
  name: string;
}

interface TimelineEvent {
  type: "creation" | "status-change" | "reminder" | "general";
  title: string;
  date: string;
  by?: string;
  details?: string;
}

interface AppointmentDetail {
  id: string;
  title: string;
  status: "confirmed" | "pending" | "cancelled" | "completed";
  patient: Patient;
  service?: Service;
  duration: string;
  date: string;
  time: string;
  start_time: string;
  location?: string; // Made optional
  type?: string; // Made optional
  reason?: string; // Made optional
  notes?: string; // Made optional
  timeline?: TimelineEvent[]; // Made optional
}

const formatAppointmentDateTime = (
  isoDateString: string,
  isoTimeString: string,
): string => {
  // FIX: Split the string and parse as Local Time
  // Date constructor uses 0-indexed months, so we subtract 1 from month
  const [year, month, day] = isoDateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  const now = new Date();

  // Get time components
  const [hourStr, minuteStr] = isoTimeString.split(":");
  let hours = parseInt(hourStr, 10);
  const suffix = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;

  // Comparison logic
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const isTomorrow =
    date.getDate() === tomorrow.getDate() &&
    date.getMonth() === tomorrow.getMonth() &&
    date.getFullYear() === tomorrow.getFullYear();

  if (isToday) {
    return `Hoy, ${hours}:${minuteStr} ${suffix}`;
  }

  if (isTomorrow) {
    return `Mañana, ${hours}:${minuteStr} ${suffix}`;
  }

  // Fallback for any other date
  const dateOptions: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
  };

  const dateString = date
    .toLocaleDateString("es-ES", dateOptions)
    .replace(/\./g, "");

  return `${dateString}, ${hours}:${minuteStr} ${suffix}`;
};

const AppointmentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState<AppointmentDetail | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modal, setModal] = useState<{
    open: boolean;
    type: "success" | "error";
    message: string;
  }>({ open: false, type: "success", message: "" });

  useEffect(() => {
    const fetchAppointmentDetail = async () => {
      try {
        setLoading(true);
        // Make the API call and await the response
        const response = await api.get(`/appointments/${id}/`);

        if (!response || !response.data) {
          throw new Error("Appointment not found");
        }

        // Set the appointment data from the response
        // You might need to adjust this based on your API response structure
        setAppointment(response.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAppointmentDetail();
    }
  }, [id]);

  const handleCancelClick = () => {
    setIsModalOpen(true);
  };

  const handleConfirmCancel = async () => {
    try {
      // Perform the API call
      await api.post(`/appointments/cancel/${id}/`);

      // Update local state so the UI reflects "Cancelada" immediately
      if (appointment) {
        setAppointment({
          ...appointment,
          status: "confirmed", // or "cancelled" depending on your logic,
          // you mentioned status: "cancelled" earlier
        } as AppointmentDetail);
        // Note: Re-fetch or manually update to "cancelled"
        setAppointment((prev) =>
          prev ? { ...prev, status: "cancelled" } : null,
        );
        setModal({
          open: true,
          type: "success",
          message: "Se ha cancelado tu cita.",
        });
      }

      setIsModalOpen(false); // Close modal on success
    } catch (err) {
      // alert("Error al cancelar la cita.");
      setModal({
        open: true,
        type: "error",
        message: "Error al cancelar la cita.",
      });
      // setIsModalOpen(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "confirmed":
        return "badge confirmed";
      case "pending":
        return "badge pending";
      case "cancelled":
        return "badge cancelled";
      case "completed":
        return "badge completed";
      default:
        return "badge";
    }
  };

  const getTimelineItemClass = (type: string) => {
    switch (type) {
      case "creation":
        return "timeline-item creation";
      case "status-change":
        return "timeline-item status-change";
      default:
        return "timeline-item";
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error || !appointment) {
    // return <div>Error: {error || "Appointment not found"}</div>;
    return (
      <Error403Page
        code={500}
        title="Recurso no disponible."
        message="Hemos tenido problemas para acceder al recurso."
        homeHref="/"
        loginHref="/login"
        supportHref="/support"
        onHomeClick={(e) => {
          e.preventDefault();
          navigate("/");
        }}
        onLoginClick={(e) => {
          e.preventDefault();
          navigate("/login");
        }}
      />
    );
  }

  return (
    <div
      className="container agenda-content full-width-main"
      id="main-content-appointment-detail"
    >
      <div className="main-calendar-view appointment-detail-view">
        {/* Header Card */}
        <div className="detail-header-card">
          <div className="patient-profile">
            <div className="patient-info">
              <h2 className="patient-name">{appointment.title}</h2>
              <span
                className={`badge status-badge ${getStatusBadgeClass(
                  appointment.status,
                )}`}
              >
                {appointment.status === "confirmed" && "Confirmada"}
                {appointment.status === "pending" && "Pendiente"}
                {appointment.status === "cancelled" && "Cancelada"}
                {appointment.status === "completed" && "Completada"}
              </span>
            </div>
          </div>
          <div className="detail-actions">
            <button
              className="btn btn--secondary"
              onClick={() => navigate("/appointments")}
            >
              <i className="fas fa-arrow-left"></i> Volver a las citas
            </button>
            <button
              className="btn btn--secondary"
              onClick={() => navigate(`/appointment/edit/${id}`)}
            >
              <i className="fas fa-edit"></i> Editar Cita
            </button>
          </div>
        </div>

        <div className="detail-grid">
          {/* Appointment Information Card */}
          <div className="detail-card">
            <h3 className="card-title">
              <i className="fas fa-calendar-alt"></i> Información de la Cita
            </h3>

            <div className="appointment-meta">
              <div className="meta-item">
                <i className="fas fa-user"></i>
                <span>
                  Paciente: <strong>{appointment.patient?.name ?? "-"}</strong>
                </span>
              </div>
              <div className="meta-item">
                <i className="fas fa-stethoscope"></i>
                <span>
                  Servicio: <strong>{appointment.service?.name ?? "-"}</strong>
                </span>
              </div>
              {/* Removed doctor field */}
              <div className="meta-item">
                <i className="fas fa-clock"></i>
                <span>
                  Duración: <strong>{appointment.duration}</strong>
                </span>
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Fecha de la Cita</label>
                <p className="detail-value">
                  {formatAppointmentDateTime(
                    appointment.date,
                    appointment.time,
                  )}
                </p>
              </div>
              {/* Conditionally render location if it exists */}
              {appointment.location && (
                <div className="form-group">
                  <label>Ubicación</label>
                  <p className="detail-value">{appointment.location}</p>
                </div>
              )}
              {/* Conditionally render type if it exists */}
              {appointment.type && (
                <div className="form-group">
                  <label>Tipo de Cita</label>
                  <p className="detail-value">{appointment.type}</p>
                </div>
              )}
              {/* Conditionally render reason if it exists */}
              {appointment.reason && (
                <div className="form-group full-width">
                  <label>Motivo de la Consulta</label>
                  <p className="detail-value">{appointment.reason}</p>
                </div>
              )}
            </div>

            {/* Conditionally render notes if they exist */}
            {appointment.notes && (
              <div className="appointment-notes">
                <h4>
                  <i className="fas fa-sticky-note"></i> Notas Adicionales
                </h4>
                <p>{appointment.notes}</p>
              </div>
            )}
          </div>

          {/* Timeline and Actions Card */}
          <div className="detail-card">
            <h3 className="card-title">
              <i className="fas fa-history"></i> Historial de la Cita
            </h3>

            {/* Conditionally render timeline if it exists */}
            {appointment.timeline && appointment.timeline.length > 0 ? (
              appointment.timeline.map((event, index) => (
                <div key={index} className={getTimelineItemClass(event.type)}>
                  <strong>{event.title}</strong>
                  <span>
                    {" "}
                    {event.date}
                    {event.details && ` - ${event.details}`}
                  </span>
                </div>
              ))
            ) : (
              <p>No hay historial disponible para esta cita.</p>
            )}

            <h4 className="card-title" style={{ marginTop: "2rem" }}>
              <i className="fas fa-tasks"></i> Acciones
            </h4>
            <div className="action-buttons">
              {/* <button className="btn btn--secondary">
                <i className="fas fa-envelope"></i> Enviar Recordatorio
              </button> */}
              <button
                className="btn btn--secondary"
                onClick={handleCancelClick}
                disabled={appointment.status === "cancelled"}
              >
                <i className="fas fa-times-circle"></i> Cancelar Cita
              </button>
            </div>
          </div>
        </div>
      </div>
      <PublicConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmCancel}
        title="¿Cancelar esta cita?"
        message="¿Estás seguro? Esta acción notificará al paciente sobre la cancelación y liberará el horario."
        confirmText="Confirmar cancelación"
        cancelText="Volver atrás"
      />
      <AlertModal
        type={modal.type}
        message={modal.message}
        isOpen={modal.open}
        onClose={() => setModal((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default AppointmentDetail;
